/**
 * Real User Monitoring — Web Vitals Reporter
 *
 * Collects Core Web Vitals (LCP, FID/INP, CLS, FCP, TTFB) and sends them
 * to configured analytics backends.  Uses the `web-vitals` library when
 * available, with a graceful fallback to no-op if the library is missing.
 *
 * This module is designed to be imported in a client component only.
 */

import { analytics } from "./provider";
import type { EventName, EventProperties } from "./events";

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface WebVitalMetric {
  /** Metric name: CLS, FCP, FID, INP, LCP, TTFB */
  name: string;
  /** Metric value */
  value: number;
  /** Rating: "good" | "needs-improvement" | "poor" */
  rating: "good" | "needs-improvement" | "poor";
  /** Delta from previous report */
  delta: number;
  /** Unique ID for the metric entry */
  id: string;
  /** Navigation type */
  navigationType?: string;
}

/* -------------------------------------------------------------------------- */
/*  Reporter                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Report a single web vital metric to analytics.
 * Called by the WebVitalsReporter component for each metric callback.
 */
export function reportWebVital(metric: WebVitalMetric): void {
  // Log in dev for debugging
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug(
      `[WebVital] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
    );
  }

  // Send to analytics provider as a custom event
  analytics.track("web_vital" as EventName, {
    vitalName: metric.name,
    vitalValue: metric.value,
    vitalRating: metric.rating,
    component: "web-vitals",
  } as unknown as EventProperties<EventName>);

  // Also send to the /api/vitals beacon endpoint if configured
  const endpoint = process.env.NEXT_PUBLIC_VITALS_ENDPOINT;
  if (endpoint && typeof navigator !== "undefined" && navigator.sendBeacon) {
    const body = JSON.stringify({
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      page: typeof window !== "undefined" ? window.location.pathname : undefined,
      timestamp: Date.now(),
    });
    navigator.sendBeacon(endpoint, body);
  }
}

/* -------------------------------------------------------------------------- */
/*  CWV Thresholds for reference                                               */
/* -------------------------------------------------------------------------- */

/**
 * Core Web Vitals thresholds (Google 2024).
 * Used for local assertions and dashboards.
 */
export const CWV_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
} as const;

/** Check if a metric value meets the "good" threshold. */
export function isGoodVital(
  name: keyof typeof CWV_THRESHOLDS,
  value: number,
): boolean {
  const threshold = CWV_THRESHOLDS[name];
  return value <= threshold.good;
}

/** Get the rating for a metric value. */
export function getVitalRating(
  name: keyof typeof CWV_THRESHOLDS,
  value: number,
): "good" | "needs-improvement" | "poor" {
  const threshold = CWV_THRESHOLDS[name];
  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
}
